import {
  DataStatus,
  showNotification,
  FormField,
  Form,
  FormSubmitHandler,
  ButtonGroup,
  FormButton,
  Separator,
  Card,
  TextInput,
  NumberInput,
  DatePicker,
} from 'platform/components';
import {Grid, Heading, Text, VStack} from 'platform/foundation';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {
  useGetServiceJobIntegrationXentryInformationQuery,
  usePatchServiceJobIntegrationXentryInformationMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {Nullish, parseDate, suffixTestId, TestIdProps} from 'shared/web';

import {GROUNDING_REASON_OPTIONS} from '../../../constants/groundingReasonOptions';
import {XENTRY_POSITION_TYPE_OPTIONS} from '../../../constants/xentryPositionTypeOptions';
import {
  XentryInformationFormSchema,
  XentryInformationFormType,
} from '../types/XentryInformationFormType';

interface JobXentryInformationProps extends TestIdProps {
  orderId: string;
  jobId: string;
  serviceCaseId: string;
  isReadOnly?: boolean;
  isServiceCampaign?: boolean | Nullish;
}

export function JobXentryInformation(props: JobXentryInformationProps) {
  const {
    data: jobXentryInformation,
    isError: isJobXentryInformationError,
    isLoading: isJobXentryInformationLoading,
  } = useGetServiceJobIntegrationXentryInformationQuery(
    {
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.orderId,
      serviceJobId: props.jobId,
    },
    {refetchOnMountOrArgChange: true}
  );
  const [patchServiceJobIntegrationXentryInformation] =
    usePatchServiceJobIntegrationXentryInformationMutation();

  const handleSubmit: FormSubmitHandler<XentryInformationFormType> = async (data) =>
    await patchServiceJobIntegrationXentryInformation({
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.orderId,
      serviceJobId: props.jobId,
      body: {
        damageCode: data.damageCode ?? null,
        xentryPositionType: data.xentryPositionType ?? null,
        eskulabDecision: jobXentryInformation?.eskulabDecision ?? null,
        groundedVehicle: {
          isGrounded: data.isGrounded ?? false,
          groundingReason: data.groundingReason ?? null,
        },
      },
    })
      .unwrap()
      .then(() => showNotification.success())
      .catch(handleApiError);

  return (
    <DataStatus isLoading={isJobXentryInformationLoading} isError={isJobXentryInformationError}>
      <VStack spacing={1}>
        <Heading size={4}>{i18n.t('entity.serviceOrder.labels.xentryIntegration')}</Heading>
        <Text size="xSmall" color="secondary">
          {i18n.t('entity.serviceOrder.labels.xentryIntegrationDescription')}
        </Text>
      </VStack>
      <Separator />
      <Form<XentryInformationFormType>
        onSubmit={handleSubmit}
        experimentalZodSchema={XentryInformationFormSchema}
        defaultValues={{
          damageCode: jobXentryInformation?.damageCode ?? undefined,
          xentryPositionType: jobXentryInformation?.xentryPositionType ?? undefined,
          isGrounded: jobXentryInformation?.groundedVehicle?.isGrounded ?? false,
          groundingReason: jobXentryInformation?.groundedVehicle?.groundingReason ?? undefined,
        }}
      >
        {(control, formApi) => {
          const isRequestInformationDisabled = !!(props.isReadOnly || props.isServiceCampaign);
          const isGroundedSectionDisabled =
            props.isReadOnly || !jobXentryInformation?.groundedVehicle?.isEditable;
          const isGroundedValue = formApi.watch('isGrounded') ?? false;
          const isPositionTypeSet = isNotNilOrEmpty(formApi.watch('xentryPositionType'));
          return (
            <VStack spacing={4}>
              <Grid columns={4} spacing={4}>
                <FormField
                  control={control}
                  name="damageCode"
                  type="text"
                  label={i18n.t('entity.serviceOrder.labels.damageCode')}
                  maxLength={9}
                  isCounterVisible
                  isDisabled={isRequestInformationDisabled}
                  data-testid={suffixTestId('damageCode', props)}
                />
                <FormField
                  control={control}
                  name="xentryPositionType"
                  type="choice"
                  options={XENTRY_POSITION_TYPE_OPTIONS}
                  label={i18n.t('entity.orderRequest.labels.xentryPositionType')}
                  placeholder={i18n.t('general.labels.select')}
                  isNotClearable={isPositionTypeSet}
                  isDisabled={isRequestInformationDisabled}
                  data-testid={suffixTestId('xentryPositionType', props)}
                />
              </Grid>
              <Card
                title={i18n.t('entity.serviceOrder.labels.eskulabDecision')}
                variant="inlineGrey"
                tooltip={i18n.t('entity.serviceOrder.labels.eskulabDecisionTooltip')}
              >
                <Grid columns={4} spacing={4}>
                  <TextInput
                    label={i18n.t('entity.serviceOrder.labels.authorizationNumber')}
                    value={
                      jobXentryInformation?.eskulabDecision?.authorizationNumber?.toString() ?? null
                    }
                    isDisabled
                  />
                  <TextInput
                    label={i18n.t('entity.serviceOrder.labels.statusCode')}
                    value={jobXentryInformation?.eskulabDecision?.statusCode ?? null}
                    isDisabled
                  />
                  <DatePicker
                    label={i18n.t('entity.serviceOrder.labels.validityDate')}
                    value={parseDate(jobXentryInformation?.eskulabDecision?.validityDate)}
                    isDisabled
                  />
                  <NumberInput
                    label={i18n.t('entity.serviceOrder.labels.validityMileage')}
                    value={jobXentryInformation?.eskulabDecision?.validityMileage ?? null}
                    isDisabled
                    suffix={i18n.t('general.metric.km')}
                  />
                </Grid>
              </Card>
              <Card
                title={i18n.t('entity.serviceOrder.labels.groundedVehicle')}
                variant="inlineGrey"
                tooltip={jobXentryInformation?.groundedVehicle?.tooltip ?? null}
                control={{
                  isDisabled: isGroundedSectionDisabled,
                  type: 'switch',
                  value: isGroundedValue,
                  onChange: () => {
                    const newValue = !isGroundedValue;
                    formApi.setValue('isGrounded', newValue);
                    if (!newValue) {
                      formApi.setValue('groundingReason', null);
                    }
                  },
                }}
              >
                <Grid columns={2} spacing={4}>
                  <FormField
                    control={control}
                    name="groundingReason"
                    type="choice"
                    options={GROUNDING_REASON_OPTIONS}
                    label={i18n.t('entity.serviceOrder.labels.groundingReason')}
                    placeholder={i18n.t('general.labels.select')}
                    isDisabled={isGroundedSectionDisabled || !isGroundedValue}
                  />
                </Grid>
              </Card>
              <ButtonGroup align="right">
                <FormButton
                  control={control}
                  type="submit"
                  title={i18n.t('general.actions.saveChanges')}
                  isDisabled={props.isReadOnly}
                  data-testid={suffixTestId('save', props)}
                />
              </ButtonGroup>
            </VStack>
          );
        }}
      </Form>
    </DataStatus>
  );
}
